import React, { useState } from 'react';
import { Outlet, Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { FooterContactForm } from './FooterContactForm';
import { FooterSocialLinks } from './FooterSocialLinks';
import { JoinNewsletterButton } from './NewsletterSignup';

const LOGO_FULL = 'https://customer-assets-wrfwihn1.emergentagent.net/wingman/e3dd0bb5-cd52-4986-8bcb-0b93e872dd3d/attachments/a3c52e10cea441228c71b76a0bf010e8_pbb640.png';
const LOGO_ICON = 'https://customer-assets-wrfwihn1.emergentagent.net/wingman/e3dd0bb5-cd52-4986-8bcb-0b93e872dd3d/attachments/9becdabf3a30463bb4aa2472b0edd819_AIMG_7939%20150.png';

const NAV = [
  { to: '/plans', label: 'Choose Plan', highlight: true },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/blackout/info', label: 'Blackout Game', spark: true },
  { to: '/directory', label: 'Directory' },
  { to: '/events', label: 'Events' },
  { to: '/community', label: 'Community' },
  { to: '/auction', label: 'C.A.N. Auction' },
  { to: '/store', label: 'Online Store' },
  { to: '/coupons', label: 'Coupons' },
  { to: '/loyalty', label: 'Loyalty' },
  // /dating is wrapped in <Protected> in App.js — logged-out clicks are
  // auto-redirected to /login?redirect=/dating, so the same nav entry works
  // for authenticated and unauthenticated visitors.
  { to: '/dating', label: 'Dating' },
];

/** Public shell — cream background, white nav, PBB branding.
 *  Wraps all public routes visible to logged-out visitors. */
export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#faf7f1] text-neutral-900">
      <div className="bg-green-700 text-white" data-testid="nl-topbar">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-3 text-xs sm:text-sm">
          <span className="hidden sm:inline">Black business news, events & deals — every week.</span>
          <JoinNewsletterButton testId="join-newsletter-topbar" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300" />
        </div>
      </div>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/" data-testid="public-logo" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_ICON} alt="PBB" className="h-9 w-9 md:hidden"/>
            <img src={LOGO_FULL} alt="Promote Black Business" className="hidden md:block h-10"/>
          </Link>
          <nav className="hidden lg:flex items-center gap-3 xl:gap-5 ml-4 flex-1">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.replace(/[^a-z]/gi,'').toLowerCase()}`}
                className={({isActive}) =>
                  n.highlight
                    ? `whitespace-nowrap text-[13px] xl:text-sm font-semibold transition-colors inline-flex items-center gap-1 px-3 py-1.5 rounded-full ${isActive ? 'bg-green-600 text-white' : 'bg-yellow-400 text-black hover:bg-yellow-300'}`
                    : `whitespace-nowrap text-[13px] xl:text-sm font-medium transition-colors ${isActive ? 'text-green-700' : 'text-neutral-700 hover:text-green-700'}`
                }>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex whitespace-nowrap px-3 xl:px-4 py-2 rounded-full border border-neutral-300 hover:border-green-600 hover:text-green-700 text-sm font-medium">Business Login</Link>
            <Link to="/signup" data-testid="nav-signup" className="inline-flex whitespace-nowrap px-3 xl:px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Register Free</Link>
            <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(v => !v)} aria-label="Menu">
              {open ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-neutral-200 bg-white">
            <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-2">
              {NAV.map(n => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({isActive}) =>
                    n.highlight
                      ? `py-2 px-3 text-sm font-semibold rounded-lg ${isActive ? 'bg-green-600 text-white' : 'bg-yellow-400 text-black'}`
                      : `py-2 text-sm font-medium ${isActive ? 'text-green-700' : 'text-neutral-700'}`
                  }>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-sm font-medium border-t border-neutral-200 pt-3 mt-1">Business Login</Link>
            </nav>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="bg-neutral-900 text-neutral-400 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-10 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src={LOGO_FULL} alt="Promote Black Business" className="h-9"/>
            <p className="mt-3 text-sm text-neutral-400 max-w-xs">A community-driven directory + rewards platform lifting up Black-owned businesses.</p>
            <FooterSocialLinks />
          </div>
          <div>
            <div className="text-white font-semibold mb-2 text-sm">Explore</div>
            <ul className="space-y-1.5 text-sm">
              {NAV.map(n => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2 text-sm">Join</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/signup" className="hover:text-white">Register free</Link></li>
              <li><Link to="/login" className="hover:text-white">Log in</Link></li>
              <li><Link to="/donate" className="hover:text-white">Donate to C.A.N.</Link></li>
              <li><Link to="/newsletter/join" className="hover:text-white" data-testid="footer-newsletter-link">Join our weekly newsletter</Link></li>
            </ul>
          </div>
          <div id="contact" data-testid="footer-contact">
            <div className="text-white font-semibold mb-2 text-sm">Contact Us</div>
            <FooterContactForm />
          </div>
        </div>
        <div className="border-t border-neutral-800 py-4 text-center text-[11px]">© {new Date().getFullYear()} Promote Black Business, LLC · All rights reserved</div>
      </footer>
    </div>
  );
}
